import type { BadgeProps } from "@/components/ui/badge";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";

/**
 * The single source of truth for how a Supplier Match card's status renders
 * (badge variant + label). Used by both the grid card and its detail dialog
 * so the two never drift.
 *
 * A `pending` status means two different things, and this is the one place
 * that tells them apart (ADR-0005): `verified_email` set (no `pending_email`)
 * is a verified email whose first lookup hasn't settled yet - self-healing,
 * nothing for the salon to do. `pending_email` set (no `verified_email`) is
 * awaiting the salon's confirmation click - nothing will happen until they
 * act.
 */
export function getSupplierMatchStatusDisplay(
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView,
  opts: { checking?: boolean } = {}
): { variant: NonNullable<BadgeProps["variant"]>; label: string } {
  if (opts.checking) {
    return { variant: "secondary", label: "Checking…" };
  }

  // `claim_blocked` (a confirm-time email collision) leaves the row as it was,
  // so for most statuses the badge keeps showing it and the card and dialog
  // add `CLAIM_EMAIL_IN_USE_MESSAGE`. The exception is a first claim: it is
  // still `pending`, but its link was just spent and nothing is awaiting
  // confirmation, so "Confirm your email" would be false.
  switch (view.status) {
    case "not_checked":
      return { variant: "neutral", label: "Not checked" };
    case "pending":
      if (view.claim_blocked) {
        return { variant: "critical", label: "Email in use" };
      }
      // On a claimable seller a pending row is always a claim awaiting its
      // confirmation click; there is no lookup to be "verifying".
      return view.pending_email || view.claimable
        ? { variant: "warning", label: "Confirm your email" }
        : { variant: "secondary", label: "Verifying…" };
    case "matched":
      return { variant: "success", label: "Matched" };
    case "not_found":
      return { variant: "critical", label: "Not found" };
    case "released":
      return { variant: "neutral", label: "Released" };
    case "claimed":
      // The claim on the old email still stands while a new email awaits its
      // confirmation click (the dialog says so), but the salon has something
      // to do, so the pill says that rather than a settled "claimed". Not when
      // the claim is blocked: a confirm-time collision leaves the just-used
      // address in `pending_email`, but its link is spent, so nothing is
      // awaiting confirmation and the standing claim is all there is.
      return view.pending_email && !view.claim_blocked
        ? { variant: "warning", label: "Pending verification" }
        : { variant: "success", label: "Account claimed" };
  }
}

/**
 * What the claim route answers (409 `email_in_use`) when another account holds
 * the email at that seller: `emailInUseError()` in the api's
 * `shared/utils/supplier-match-conflict.ts`. The SDK's error carries this
 * message but not the route's `code`, so a caller that must tell this 409 from
 * the live-match one compares against it. Keep the two in step.
 */
export const CLAIM_EMAIL_IN_USE_API_MESSAGE =
  "This email is already in use by another account.";

/** Shown on the card and in the dialog wherever a view carries `claim_blocked` (a confirm-time email collision). */
export const CLAIM_EMAIL_IN_USE_MESSAGE = `${CLAIM_EMAIL_IN_USE_API_MESSAGE} Contact support.`;

/**
 * Whether the salon already has an account with this supplier: a `claimed`
 * trade account or a `matched` lookup. Either way there is nothing to claim.
 */
export function hasSupplierMatchAccount(
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView
): boolean {
  return view.status === "claimed" || view.status === "matched";
}

/**
 * Whether the card offers "I have an account": the seller takes trade-account
 * claims and the salon holds neither a claim nor a live match. A `matched`
 * claimable seller keeps its last-known Matched badge and offers no claim
 * (ADR-0005 "Supplier scope"); the `claimable` flag, not `status`, is the key
 * because a released claim is indistinguishable from a released match.
 */
export function canClaimSupplierMatch(
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView
): boolean {
  return (
    view.claimable && view.status !== "claimed" && view.status !== "matched"
  );
}

/**
 * The emails this salon has already used with Supplier Match, for the email
 * field's suggestions: the login email first, then every verified email on the
 * grid's cards. Distinct case-insensitively (the first spelling wins), no
 * extra request - the grid is already loaded. Pending, unconfirmed addresses
 * are not suggested: they are not the salon's to reuse yet.
 */
export function collectSupplierMatchEmails(
  views: readonly SalonStockerHttpTypes.StoreSupplierMatchStatusView[],
  loginEmail?: string | null
): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const candidate of [
    loginEmail,
    ...views.map((view) => view.verified_email)
  ]) {
    const email = candidate?.trim();
    if (!email || seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());
    emails.push(email);
  }
  return emails;
}

/**
 * Whether the salon has a claim on file for this seller: a standing `claimed`
 * row, or a `pending` one awaiting its confirmation click. Either can be
 * removed or switched to a different email.
 */
export function hasSupplierMatchClaimOnFile(
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView
): boolean {
  return (
    view.status === "claimed" || (view.claimable && view.status === "pending")
  );
}

/**
 * Whether the dialog shows the trade-account claim flow rather than the
 * lookup one: a standing claim (even once its seller gained an integration,
 * until the claim-to-match follow-up lands) or a claimable seller the salon
 * can still claim.
 */
export function isSupplierMatchClaimView(
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView
): boolean {
  return view.status === "claimed" || canClaimSupplierMatch(view);
}

/**
 * Whether the card has anything to do right now (a lookup, a claim, or a
 * claim to remove). A card with nothing to do renders muted but stays
 * clickable so the dialog can say why.
 */
export function isSupplierMatchActionable(
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView
): boolean {
  return view.lookup_capable || isSupplierMatchClaimView(view);
}

/**
 * Whether a card is currently eligible for the grid's auto-check pass -
 * purely a client-side rendering condition (mirrors the shape of the
 * backend's `planSupplierMatchCheck` eligibility, not a duplicate of its
 * logic): a lookup-capable seller with no settled outcome, or a verified
 * `pending` row awaiting its first settle. Never a `claimed` row or a
 * `claimable` seller (KAN-322).
 */
export function isSupplierMatchCheckEligible(
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView
): boolean {
  // A claim is never looked up, and a claimable seller has nothing to look
  // up against (the API refuses both with 400).
  if (!view.lookup_capable || view.claimable || view.status === "claimed") {
    return false;
  }
  if (view.status === "not_checked") {
    return true;
  }
  return view.status === "pending" && !!view.verified_email;
}
